import { Roles } from '@common/types/user';
import { StyledMenuItem } from '@components/display/menu';
import { Popper } from '@components/display/poppers';
import { APP_PREFIX_PATH } from '@constants/common';
import EventOwnerDialog, { EventOwnerDialogRef } from '@event/components/display/menus/EventOwnerDialog';
import {
    PrintingSettingsDialog,
    PrintingSettingsDialogRef,
} from '@event/components/display/menus/PrintingSettingsDialog';
import { useEventDashboardContext } from '@event/contexts/EventContext';
import { useCopyEventMutation } from '@event/services/events';
import { EventTypes } from '@event/types/event';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import SupervisedUserCircleOutlinedIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import {
    CircularProgress,
    ClickAwayListener,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuList,
    Tooltip,
} from '@mui/material';
import { handleError, toaster } from '@utils/common';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

interface IEventDetailMenuProps {
    onDeleteClick: () => void;
    hash: string;
}

const EventDetailMenu = ({ onDeleteClick, hash }: IEventDetailMenuProps) => {
    const [open, setOpen] = useState(false);

    const eventOwnerDialogRef = useRef<EventOwnerDialogRef>(null);
    const printingSettingsDialogRef = useRef<PrintingSettingsDialogRef>(null);

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const ref = useRef<HTMLButtonElement>(null);

    const { event, user } = useEventDashboardContext();
    const isOrganizer = useMemo(() => event?.owner?.id === user?.id, [event?.owner, user?.id]);
    const isAdmin = useMemo(() => user?.role === Roles.VICTRON, [user?.role]);

    const [copyEvent, { isLoading: isCopyEventLoading }] = useCopyEventMutation();

    const handleDelete = () => {
        onDeleteClick();
        setOpen(false);
    };

    const copyEventHash = () => {
        navigator.clipboard.writeText(hash);
        setOpen(false);
        toaster.success({
            msg: 'Event hash copied to clipboard',
        });
    };

    const handleCopyEvent = async () => {
        try {
            const { id } = await copyEvent({ id: Number(eventId), type: eventType }).unwrap();
            toaster.success({
                msg: 'Event successfully copied',
                linkProps: {
                    to: `/dashboard/events/${eventType}/${id}`,
                    target: '_blank',
                    hrefText: 'View event',
                },
            });
            window.open(`${APP_PREFIX_PATH}/dashboard/events/${eventType}/${id}`, '_blank');
            setOpen(false);
        } catch (err) {
            handleError('Error copying event', err);
        }
    };

    return (
        <>
            <Tooltip title={!isOrganizer && !isAdmin ? 'This is only available for event organizers' : ''}>
                <span>
                    <IconButton
                        ref={ref}
                        onClick={() => setOpen(true)}
                        disabled={!isOrganizer && !isAdmin}
                        className="p-0"
                    >
                        <MoreHorizIcon sx={{ fill: 'black' }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Popper arrow={false} open={open} anchorEl={ref.current} placement="bottom" transition>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <MenuList autoFocusItem={open} className="rounded-lg py-0 min-w-[255px]">
                        <StyledMenuItem className="text-rose-500" onClick={handleDelete}>
                            <ListItemIcon>
                                <DeleteOutlinedIcon color="error" />
                            </ListItemIcon>
                            <ListItemText>Remove</ListItemText>
                        </StyledMenuItem>
                        <StyledMenuItem onClick={copyEventHash}>
                            <ListItemIcon>
                                <ContentCopyOutlined />
                            </ListItemIcon>
                            <ListItemText>Copy Event Hash</ListItemText>
                        </StyledMenuItem>
                        <StyledMenuItem onClick={handleCopyEvent} disabled={isCopyEventLoading}>
                            <ListItemIcon>
                                {isCopyEventLoading ? <CircularProgress size={20} /> : <FileCopyOutlinedIcon />}
                            </ListItemIcon>
                            <ListItemText>Duplicate Event</ListItemText>
                        </StyledMenuItem>
                        <StyledMenuItem
                            onClick={() => {
                                printingSettingsDialogRef.current?.open();
                                setOpen(false);
                            }}
                        >
                            <ListItemIcon>
                                <PrintOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText>Printing Settings</ListItemText>
                        </StyledMenuItem>
                        <StyledMenuItem
                            underline={false}
                            onClick={() => {
                                eventOwnerDialogRef.current?.open();
                                setOpen(false);
                            }}
                        >
                            <ListItemIcon>
                                <SupervisedUserCircleOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText>Change Event Owner</ListItemText>
                        </StyledMenuItem>
                    </MenuList>
                </ClickAwayListener>
            </Popper>
            <EventOwnerDialog ref={eventOwnerDialogRef} />
            <PrintingSettingsDialog ref={printingSettingsDialogRef} />
        </>
    );
};

export { EventDetailMenu };
